/**
 * audit-styles.ts — the audit trail room's own rules.
 *
 * ## Greyscale, with one exception
 *
 * Colour is attention, not category. A policy refusal in the trail is the
 * policy working, and a row painted red for it would teach people to read the
 * trail as a list of incidents. So every row is grey whatever it records.
 *
 * The one exception is an entry the server's own check says was altered after
 * it was written. That needs a person, which is what ochre means, so it gets
 * ochre and a sentence saying why. It never gets green for passing: nothing on
 * this screen can check a pass, so it is not decorated as one.
 *
 * ## Two widths, one list
 *
 * A phone gets a card per entry, each field labelled in text beside it. From
 * 720px the same markup becomes a table with a header row, and from 1280px it
 * gains a column for the product, machine and model — so a 4K screen spends
 * its width on facts rather than on margin. Container queries rather than
 * media queries, because the list sits inside a dialog whose width is not the
 * window's.
 *
 * ## The traps this file is written around
 *
 * It is a template literal, so a backtick anywhere in it — a comment included —
 * ends the string and the build error lands somewhere innocent. And no var()
 * carries a fallback: a fallback hides a token that does not exist, which is
 * what the stylesheet test beside the room checks for.
 *
 * Every class here is its own. Nothing redefines a class from the main sheet,
 * because this sheet is injected by the room and may land before or after the
 * main one, and a tie on specificity would then be decided by mount order.
 */

import { useSheet } from './placement.ts';

export const AUDIT_CSS = `
.c42-audit{container-name:c42-audit-room;container-type:inline-size;margin:0 auto}
.c42-audit *{box-sizing:border-box}
/* Prose reads at a measure; the list below it takes the whole width. */
.c42-audit .c42-phd p{max-width:80ch}
/* Which part of the trail this is, and who reads more. Grey like the rest:
   a person seeing their own entries is the rule working, not a fault. */
.c42-audit-view{margin-top:6px;font-size:12.5px;line-height:1.5;color:var(--c42-ink-dim)}
.c42-audit-view b{font-weight:600;color:var(--c42-ink)}
.c42-audit-top{display:grid;grid-template-columns:minmax(0,1fr);gap:0 28px;align-items:start}

/* What the trail cannot answer. Information, so grey: it is a property of the
   trail, not something wrong with it. */
.c42-audit-limits{border:1px solid var(--c42-line-soft);border-radius:var(--c42-radius);background:var(--c42-sunk);
  padding:12px 14px;margin-bottom:16px;max-width:80ch;font-size:12.5px;line-height:1.5;color:var(--c42-ink-dim)}
.c42-audit-limits b{display:block;font-size:13px;color:var(--c42-ink);margin-bottom:6px}
.c42-audit-limits ul{margin:0;padding-left:18px;display:flex;flex-direction:column;gap:6px}
.c42-audit-limits em{font-style:normal;color:var(--c42-ink)}

/* The toolbar: two filters, what they matched, and when this was read. */
.c42-audit-bar{display:flex;flex-wrap:wrap;align-items:flex-end;gap:10px 14px;margin-bottom:10px}
.c42-audit-pick{display:flex;flex-direction:column;gap:3px;flex:0 1 260px;min-width:170px}
.c42-audit-pick label{font-size:11px;letter-spacing:.05em;text-transform:uppercase;color:var(--c42-ink-faint)}
.c42-audit-pick select{width:100%;margin:0}
.c42-audit-sum{align-self:center;font-size:12px;color:var(--c42-ink-dim)}
.c42-audit-fresh{margin-left:auto;display:flex;align-items:center;gap:8px;font-size:12px;color:var(--c42-ink-faint)}
.c42-audit-note{margin:0 0 10px;max-width:80ch;font-size:12px;color:var(--c42-ink-dim)}

/* An entry the server's check failed. See the note at the top. */
.c42-audit-broken{border:1px solid var(--c42-attn-line);border-radius:var(--c42-radius);background:var(--c42-attn-wash);
  padding:12px 14px;margin-bottom:14px;max-width:80ch;font-size:12.5px}
.c42-audit-broken b{display:block;font-size:13px;color:var(--c42-attn-strong);margin-bottom:4px}
.c42-audit-broken p{margin:0 0 8px;line-height:1.5;color:var(--c42-ink-dim)}

/* The list. No overflow rule on it: one would make it the scroll container,
   and the day headings would stop sticking to the top of the dialog. */
.c42-audit-list{container-name:c42-audit-list;container-type:inline-size;border:1px solid var(--c42-line);
  border-radius:var(--c42-radius);background:var(--c42-paper)}
/* Dimmed while a refresh is out, which is the whole of the feedback a click on
   Refresh needs before the server answers. */
.c42-audit-list[aria-busy="true"]{opacity:.55}
.c42-audit-head{display:none}
.c42-audit-day h3{position:sticky;top:0;z-index:1;margin:0;padding:7px 12px;background:var(--c42-panel);
  border-bottom:1px solid var(--c42-line-soft);font-size:11px;font-weight:600;letter-spacing:.05em;
  text-transform:uppercase;color:var(--c42-ink-faint)}
.c42-audit-day ol{list-style:none;margin:0;padding:0}

/* A card per entry, until there is room for a table. */
.c42-audit-row{display:grid;grid-template-columns:minmax(0,auto) minmax(0,1fr);
  grid-template-areas:"when what" "who who" "against against" "via via" "facts facts" "rec rec";
  gap:5px 10px;padding:10px 12px;border-bottom:1px solid var(--c42-line-soft);font-size:12.5px;color:var(--c42-ink)}
.c42-audit-day:last-child .c42-audit-row:last-child{border-bottom:0}
.c42-audit-f{min-width:0;display:flex;gap:8px;align-items:baseline}
.c42-audit-f.when{grid-area:when}
.c42-audit-f.who{grid-area:who}
.c42-audit-f.what{grid-area:what}
.c42-audit-f.against{grid-area:against}
.c42-audit-f.via{grid-area:via}
.c42-audit-f.facts{grid-area:facts}
.c42-audit-f.rec{grid-area:rec}
/* Each field says what it is in words, on a phone, where there is no header
   row to say it. The first line needs none: a time and an event read as one. */
.c42-audit-k{flex:none;width:64px;font-size:11px;color:var(--c42-ink-faint)}
.c42-audit-f.when .c42-audit-k,.c42-audit-f.what .c42-audit-k{position:absolute;width:1px;height:1px;
  overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.c42-audit-v{min-width:0;display:flex;flex-direction:column;gap:2px}
.c42-audit-time{font-family:var(--c42-font-mono);font-size:12px;color:var(--c42-ink-dim);white-space:nowrap}
/* Identifiers wrap rather than truncate: a person must be able to read all of
   one, and an ellipsis on a phone is a tooltip with no way to open it. */
.c42-audit-id{font-family:var(--c42-font-mono);font-size:11.5px;overflow-wrap:anywhere}
.c42-audit-sub{color:var(--c42-ink-dim);overflow-wrap:anywhere}
.c42-audit-you{align-self:flex-start;font-size:10.5px;padding:0 6px;border:1px solid var(--c42-line);
  border-radius:99px;color:var(--c42-ink-dim)}
.c42-audit-flag{font-weight:600;color:var(--c42-attn-strong)}
.c42-audit-row.broken{background:var(--c42-attn-wash)}
.c42-audit-facts{display:flex;flex-wrap:wrap;gap:3px 14px;margin:0}
.c42-audit-facts div{display:flex;gap:5px;min-width:0}
.c42-audit-facts dt{font-family:var(--c42-font-mono);font-size:11px;color:var(--c42-ink-faint)}
.c42-audit-facts dd{margin:0;overflow-wrap:anywhere}
.c42-audit-none{color:var(--c42-ink-faint)}
.c42-audit-recbtn{font:inherit;font-family:var(--c42-font-mono);font-size:11px;padding:0;border:0;background:none;
  color:var(--c42-ink-dim);cursor:pointer;text-decoration:underline;text-decoration-color:var(--c42-line)}
.c42-audit-recbtn:hover{color:var(--c42-ink)}
/* The whole record, as stored. Spans the row whatever the layout. */
.c42-audit-panel{grid-column:1 / -1;margin-top:6px;padding:8px 10px;border:1px solid var(--c42-line-soft);
  border-radius:var(--c42-radius);background:var(--c42-sunk)}
.c42-audit-panel dl{display:grid;grid-template-columns:max-content minmax(0,1fr);gap:3px 12px;margin:0}
/* Each name and value is wrapped in a div, which a description list allows;
   the wrapper steps out of the grid so the two line up in columns. */
.c42-audit-panel dl>div{display:contents}
.c42-audit-panel dt{font-size:11px;color:var(--c42-ink-faint)}
.c42-audit-panel dd{margin:0;font-family:var(--c42-font-mono);font-size:11.5px;overflow-wrap:anywhere}

/* Paging, and where the list ends. */
.c42-audit-pager{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:12px;font-size:12px;color:var(--c42-ink-dim)}
.c42-audit-end{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:12px;max-width:80ch;font-size:12px;color:var(--c42-ink-dim)}
.c42-audit-end p{margin:0}

@container c42-audit-list (min-width: 720px){
  .c42-audit-head,.c42-audit-row{grid-template-columns:84px minmax(0,1fr) minmax(0,1.6fr) minmax(0,1.3fr) minmax(0,1.5fr) 72px;column-gap:14px}
  .c42-audit-head{display:grid;grid-template-areas:"when who what against facts rec";padding:7px 12px;
    border-bottom:1px solid var(--c42-line);font-size:11px;letter-spacing:.05em;text-transform:uppercase;color:var(--c42-ink-faint)}
  .c42-audit-head span:nth-child(1){grid-area:when}
  .c42-audit-head span:nth-child(2){grid-area:who}
  .c42-audit-head span:nth-child(3){grid-area:what}
  .c42-audit-head span:nth-child(4){grid-area:against}
  .c42-audit-head span:nth-child(5){grid-area:facts}
  .c42-audit-head span:nth-child(6){grid-area:via;display:none}
  .c42-audit-head span:nth-child(7){grid-area:rec}
  /* The product and machine sit under what was acted on until there is a
     column for them. */
  .c42-audit-row{grid-template-areas:"when who what against facts rec" "when who what via facts rec";row-gap:2px}
  .c42-audit-f{display:block}
  .c42-audit-k{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
}
@container c42-audit-list (min-width: 1280px){
  .c42-audit-head,.c42-audit-row{grid-template-columns:84px minmax(0,1fr) minmax(0,1.8fr) minmax(0,1.4fr) minmax(0,1.8fr) minmax(0,1.1fr) 80px}
  .c42-audit-head{grid-template-areas:"when who what against facts via rec"}
  .c42-audit-head span:nth-child(6){display:block}
  .c42-audit-row{grid-template-areas:"when who what against facts via rec"}
}
/* Wide enough that the account of what the trail cannot say can sit beside
   the heading rather than pushing the list down a screen. */
@container c42-audit-room (min-width: 1500px){
  .c42-audit-top{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}
}
/* A thumb is not a pointer. */
@media (pointer:coarse){
  .c42-audit-recbtn{min-height:32px}
  .c42-audit-bar .c42-mini,.c42-audit-pager .c42-mini,.c42-audit-end .c42-mini{min-height:36px}
}
/* On a phone the two filters share a line, half each: stacked, they cost a
   screen-height of controls before the first entry. */
@media (max-width: 640px){
  .c42-audit-pick{flex:1 1 130px;min-width:0}
  .c42-audit-fresh{margin-left:0}
}
`;

/**
 * Put the room's rules in the document it is drawn into, once per document.
 *
 * The same mechanism as every plugin sheet (useSheet in placement.ts): per
 * document rather than per page, so the room can be drawn into a second window
 * and arrive styled, and before layout rather than after the first paint, so
 * the room is never laid out once without its rules.
 */
export function useAuditStyles(): void {
  useSheet('chat42-audit', AUDIT_CSS);
}
